import { ExternalLink, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useClients } from "@/api/hooks";
import { ClientStatusBadge } from "@/components/ClientStatusBadge";
import { Layout } from "@/components/Layout";
import { NewClientDialog } from "@/components/NewClientDialog";
import { SnapshotStatusBadge } from "@/components/SnapshotStatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function DashboardPage() {
	const clientsQuery = useClients();
	const [showNewDialog, setShowNewDialog] = useState(false);

	return (
		<Layout>
			<div className="mx-auto max-w-5xl space-y-6">
				<header className="flex items-center justify-between">
					<div>
						<h2 className="text-2xl font-semibold tracking-tight">Clients</h2>
						<p className="text-sm text-muted-foreground">
							Registered client portals — health polled on demand.
						</p>
					</div>
					<Button onClick={() => setShowNewDialog(true)}>
						<Plus className="mr-1 h-4 w-4" aria-hidden />
						New client
					</Button>
				</header>

				{clientsQuery.isLoading ? (
					<p className="text-sm text-muted-foreground">Loading clients…</p>
				) : clientsQuery.error ? (
					<p className="text-sm text-destructive">
						{clientsQuery.error instanceof ApiError
							? clientsQuery.error.detail
							: "Failed to load clients."}
					</p>
				) : !clientsQuery.data?.clients?.length ? (
					<Card>
						<CardHeader>
							<CardTitle>No clients yet</CardTitle>
							<CardDescription>
								Register your first client engagement to start polling their portal.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<Button onClick={() => setShowNewDialog(true)}>
								<Plus className="mr-1 h-4 w-4" aria-hidden />
								Add first client
							</Button>
						</CardContent>
					</Card>
				) : (
					<div className="grid gap-3">
						{clientsQuery.data.clients.map((c) => (
							<Card key={c.id} className="transition-shadow hover:shadow-sm">
								<CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
									<div className="min-w-0 space-y-1">
										<CardTitle className="flex items-center gap-2 text-lg">
											<Link
												to={`/clients/${c.id}`}
												className="hover:underline decoration-dotted underline-offset-4"
											>
												{c.display_name}
											</Link>
											<ClientStatusBadge status={c.status} />
										</CardTitle>
										<CardDescription className="flex items-center gap-2">
											<span className="font-mono text-xs">{c.slug}</span>
											<span aria-hidden>·</span>
											<a
												href={c.portal_url}
												target="_blank"
												rel="noopener noreferrer"
												className="inline-flex items-center gap-1 text-xs hover:underline"
											>
												{c.portal_url.replace(/^https?:\/\//, "")}
												<ExternalLink className="h-3 w-3" aria-hidden />
											</a>
										</CardDescription>
									</div>
									<SnapshotStatusBadge snapshot={c.latest_snapshot} />
								</CardHeader>
							</Card>
						))}
					</div>
				)}
			</div>
			<NewClientDialog open={showNewDialog} onOpenChange={setShowNewDialog} />
		</Layout>
	);
}
